import { GridColDef } from "@mui/x-data-grid";
import { useConveniosDispatch, useConveniosState } from "./convenios-context";
import { ACTION_TYPES, initialDataRequest } from "./convenios-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IConvenioResponse } from "../../../../model/admin-portal/response/convenio-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { IDocumentoConvenioRequest } from "../../../../model/admin-portal/request/documento-convenio-request";

export const useConveniosController = () => {
    const state = useConveniosState();
    const dispatch = useConveniosDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listConvenios(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar convênios' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const [orgaos, situacoes] = await Promise.all([
                AdminPortalAppService.listOrgaos(),
                AdminPortalAppService.listSituacoesConvenio()
            ]);
            dispatch({ type: ACTION_TYPES.setOrgaosList, payload: orgaos });
            dispatch({ type: ACTION_TYPES.setSituacoesList, payload: situacoes });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar listas de apoio (Órgão/Situação)' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getConvenio(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idOrgao: response.idOrgao,
                    idSituacaoConvenio: response.idSituacaoConvenio,
                    numeroConvenio: response.numeroConvenio,
                    nomConcedente: response.nomConcedente,
                    nomConvenente: response.nomConvenente,
                    textoObjetoConvenio: response.textoObjetoConvenio,
                    textoJustificativaConvenio: response.textoJustificativaConvenio || '',
                    valorConvenio: response.valorConvenio,
                    valorContrapartida: response.valorContrapartida ?? null,
                    valorRepassado: response.valorRepassado,
                    dtaCelebracao: response.dtaCelebracao,
                    dtaInicioVigencia: response.dtaInicioVigencia,
                    dtaFimVigencia: response.dtaFimVigencia,
                    dtaPublicacao: response.dtaPublicacao,
                    documentos: response.documentos?.map(d => ({ id: d.id, descricao: d.descricao, dtaPublicacao: d.dtaPublicacao, documentoUrl: d.documentoUrl })) || []
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar convênio' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idOrgao || !r.idSituacaoConvenio) return false;
        if (!r.numeroConvenio || r.numeroConvenio.trim() === '') return false;
        if (!r.nomConcedente || !r.nomConvenente) return false;
        if (!r.textoObjetoConvenio || r.textoObjetoConvenio.trim() === '') return false;
        if (!r.dtaCelebracao || !r.dtaInicioVigencia || !r.dtaFimVigencia || !r.dtaPublicacao) return false;
        if (!r.valorConvenio || r.valorConvenio <= 0) return false;
        if (!r.valorRepassado || r.valorRepassado <= 0) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IConvenioResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateConvenio(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Convênio atualizado com sucesso' : 'Convênio criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar convênio' });
        } finally {
            showBackDrop(false);
        }
    };

    //Cada filho é mantido só no estado local até o "Salvar" do Convênio inteiro - linhas novas usam id 0
    const saveDocumento = (doc: IDocumentoConvenioRequest, index?: number) => {
        const documentos = [...state.dataRequest.documentos];
        if (index !== undefined) documentos[index] = doc; else documentos.push(doc);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const removeDocumento = (index: number) => {
        const documentos = state.dataRequest.documentos.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'numeroConvenio', headerName: 'Nº Convênio', flex: 1, minWidth: 130 },
            { field: 'orgaoName', headerName: 'Órgão', flex: 1, minWidth: 160 },
            { field: 'nomConcedente', headerName: 'Concedente', flex: 1, minWidth: 160 },
            {
                field: 'situacaoName', headerName: 'Situação', width: 140,
                renderCell: (params) => <Chip label={params.value} size="small" />
            },
            {
                field: 'valorConvenio', headerName: 'Valor', width: 150, align: 'right', headerAlign: 'right',
                renderCell: (params) => params.value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid,
        saveDocumento,
        removeDocumento
    };
};
